import { ElementRef } from '@angular/core';

// counter for generated name-source ids, so two elements never share one
let nextHostNameSourceId = 0;

/**
 * How every generated name-source id starts. Diagnostics read it too: the number after it
 * changes every session, so `isGeneratedNameSourceId` lets a report skip such an id (df-625b549a3b7c).
 */
export const NAME_SOURCE_ID_PREFIX = 'cs-name-source-';

/**
 * A new, unused `cs-name-source-N` id.
 *
 * Shared by every naming helper (`row-name-source.ts`, `parent-name-source.ts` and this file):
 * two counters would hand out the same number twice, and two elements with one id make every
 * `aria-labelledby` pointing at it name whichever comes first in the document.
 */
export function newNameSourceId(): string {
  return `${NAME_SOURCE_ID_PREFIX}${nextHostNameSourceId++}`;
}

/** Whether `id` was generated by `newNameSourceId` rather than written by a developer. */
export function isGeneratedNameSourceId(id: string): boolean {
  return id.startsWith(NAME_SOURCE_ID_PREFIX);
}

/**
 * The attribute a naming helper puts on a capture-cover button it manages.
 *
 * `CsCaptureCoverDirective` names every capture-cover button from its parent by itself (see
 * `syncButtonNameFromParent`), and the buttons inside cs-button, cs-checkbox and the rest carry
 * that directive too. The helper's rule must win for those, so each helper marks its button and
 * the directive leaves a marked button alone.
 */
export const NAME_OWNER_ATTRIBUTE = 'data-cs-name-owner';

/**
 * Marks `button` as named by a helper, so the directive's own naming stays out of it.
 */
export function claimButtonNaming(button: Element): void {
  if (!button.hasAttribute(NAME_OWNER_ATTRIBUTE)) {
    button.setAttribute(NAME_OWNER_ATTRIBUTE, '');
  }
}

/**
 * Gives a component's inner `<button cs-capture-cover>` an accessible name taken from the text
 * drawn on the component's host, by pointing its `aria-labelledby` at the host.
 *
 * A capture-cover button is empty: the text the user sees (a label, projected content, a
 * description) is drawn beside it on the host, not inside it. An accessible name is computed
 * from an element's own subtree, so without this the focused button has no name and a screen
 * reader says only "button". Pointing `aria-labelledby` at the host makes the browser compute
 * the name from the host's whole visible text instead.
 *
 * Uses the host's own id when it has one, and gives it a unique generated id otherwise. When
 * the caller passes an explicit `ariaLabel` (bound as `aria-label` on the button), that name
 * wins and the `aria-labelledby` is removed.
 *
 * Call it from the component's `ngAfterViewChecked`. That hook runs every time the PARENT view
 * is checked, even for an OnPush component whose own inputs did not change — which matters
 * because a parent may re-bind the host's id at any time (the intake form's Next button
 * changes its id at every step), and a name source pointing at an old id names nothing.
 */
export function syncButtonNameFromHost(elementRef: ElementRef, ariaLabel: string | undefined): void {
  const host = elementRef.nativeElement as HTMLElement;
  const button = host.querySelector(':scope > button[cs-capture-cover]');
  // if the template has not rendered its button, there is nothing to name
  if (!button) {
    return;
  }
  claimButtonNaming(button);
  // if the caller gave an explicit name, it is on the button already as aria-label
  if (ariaLabel) {
    button.removeAttribute('aria-labelledby');
    return;
  }
  // if the host has no id yet, give it one nothing else uses
  if (!host.id) {
    host.id = newNameSourceId();
  }
  if (button.getAttribute('aria-labelledby') !== host.id) {
    button.setAttribute('aria-labelledby', host.id);
  }
}
